import { Layer, picking, project32 } from '@deck.gl/core';
import { IconLayer } from '@deck.gl/layers';
import vs from '@deck.gl/layers/src/icon-layer/icon-layer-vertex.glsl';
import { Texture2D } from '@luma.gl/core';
import { memoize } from 'lodash';
import type { RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import { DEVICE_PIXEL_RATIO, type WebGLContext } from 'src/lib/player/constants';
import { hasMouse, make2DCanvas } from 'src/lib/player/utils';
import MarkerContext from './MarkerContext';

const fs = `\
#define SHADER_NAME icon-layer-fragment-shader
precision highp float;
uniform float opacity;
uniform sampler2D iconsTexture;
uniform float alphaCutoff;
varying float vColorMode;
varying vec4 vColor;
varying vec2 vTextureCoords;
varying vec2 uv;
void main(void) {
  geometry.uv = uv;
  vec4 texColor = texture2D(iconsTexture, vTextureCoords);
  // Recolor texture with given color
  vec3 color = texColor.rgb * vColor.rgb;
  // Take the global opacity and the alpha from vColor into account for the alpha component
  float a = texColor.a * opacity * vColor.a;
  if (a < alphaCutoff) {
    discard;
  }
  gl_FragColor = vec4(color, a);
  DECKGL_FILTER_COLOR(gl_FragColor, geometry);
}
`;

const getParticleTexture = memoize(function getParticleTexture(gl: WebGLContext): Texture2D {
  const particleMeasurement = MarkerContext.measureParticle();
  const { canvas, context } = make2DCanvas(
    particleMeasurement.x * DEVICE_PIXEL_RATIO * 2,
    particleMeasurement.y * DEVICE_PIXEL_RATIO * 2,
    DEVICE_PIXEL_RATIO * 2,
  );
  MarkerContext.drawParticle(context, 1);
  return new Texture2D(gl, { data: canvas });
});

const getInstanceIconFrames = memoize((length: number): Float32Array => {
  const frames = new Float32Array(length * 4);
  for (let i = 0; i < length; i++) {
    frames[i * 4] = 0;
    frames[i * 4 + 1] = 0;
    frames[i * 4 + 2] = 26 * DEVICE_PIXEL_RATIO;
    frames[i * 4 + 3] = 26 * DEVICE_PIXEL_RATIO;
  }
  return frames;
});
const getInstanceOffsets = memoize((length: number): Float32Array => new Float32Array(length * 2));
const getInstanceColorModes = memoize(
  (length: number): Uint8ClampedArray => new Uint8ClampedArray(length),
);

class ParticleIconLayer extends IconLayer {
  static componentName = 'ParticleIconLayer';

  getShaders() {
    return Layer.prototype.getShaders.call(this, {
      vs,
      fs,
      modules: [project32, picking],
    });
  }
}

type StarterParticleLayerProps = {
  renderInfoBuffer: RenderInfoBuffer;
  gl: WebGLContext;
  onClick: (index: number) => void;
  onHover: (index: number) => void;
};

export default function createStarterParticleLayer({
  renderInfoBuffer,
  gl,
  onClick,
  onHover,
}: StarterParticleLayerProps): IconLayer | null {
  if (gl == null) return null;

  const positions = renderInfoBuffer.positions;
  const colors = new Uint8ClampedArray(renderInfoBuffer.colors);
  for (let i = 0; i < renderInfoBuffer.length; i++) {
    if (!renderInfoBuffer.getShowParticle(i)) {
      colors[i * 4 + 3] = 0;
    }
  }

  // @ts-ignore: TS2554
  return new ParticleIconLayer({
    id: 'starter-particles',
    data: {
      length: renderInfoBuffer.length,
      attributes: {
        instancePositions: { value: positions, size: 3, stride: 4 * 6 },
        instanceColors: { value: colors, size: 4 },
        instanceColorModes: {
          value: getInstanceColorModes(renderInfoBuffer.length),
          size: 1,
        },
        instanceOffsets: {
          value: getInstanceOffsets(renderInfoBuffer.length),
          size: 2,
        },
        instanceIconFrames: {
          value: getInstanceIconFrames(renderInfoBuffer.length),
          size: 4,
        },
      },
    },
    billboard: true,
    pickable: true,
    getSize: 13,
    iconAtlas: getParticleTexture(gl),
    onClick: ({ index }) => {
      onClick(index);
    },
    onHover: hasMouse()
      ? ({ index }) => {
          onHover(index);
        }
      : null,
  });
}
